import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  AIAnalysisSummary,
  FeaturesTable,
  MassSpectrum,
  MaterialDistribution,
  type MZRTDensity,
  MZRTPlot,
  SampleInfo,
  SimilarCaseDB,
  SpectrumComparison,
  SpectrumHitList,
  TICPlotUplot,
} from "@/components/charts";
import AnalysisBreadcrumb from "@/components/dashboard/AnalysisBreadcrumb";
import AnalysisGapsNote from "@/components/dashboard/AnalysisGapsNote";
import AnalysisLoadingState from "@/components/dashboard/AnalysisLoadingState";
import AnalysisProgressIndicator from "@/components/dashboard/AnalysisProgressIndicator";
import ConclusionStripV2 from "@/components/dashboard/ConclusionStripV2";
import DemoScenarioBanner from "@/components/dashboard/DemoScenarioBanner";
import FactSummaryCard from "@/components/dashboard/FactSummaryCard";
import FeatureDetailInlineRow from "@/components/dashboard/FeatureDetailInlineRow";
import FeatureDetailPanel from "@/components/dashboard/FeatureDetailPanel";
import SampleInfoCollapsedHeader from "@/components/dashboard/SampleInfoCollapsedHeader";
import SecondaryInsightsPeek from "@/components/dashboard/SecondaryInsightsPeek";
import {
  SidebarCollapseButton,
  SidebarRail,
} from "@/components/dashboard/SidebarCollapse";
import {
  useAnalysisDetail,
  useAssessments,
  useCreateAssessment,
  useRefetchChartsOnComplete,
} from "@/hooks/useAnalysis";
import { useAuth } from "@/hooks/useAuth";
import { useChartInteraction } from "@/hooks/useChartInteraction";
import {
  type DemoScenario,
  useMockupFallback,
} from "@/hooks/useMockupFallback";
import { usePersistentBoolean } from "@/hooks/usePersistentBoolean";
import { useDownloadAnalysisPptx } from "@/hooks/useReports";
import {
  useSpectrumComparison,
  useSpectrumHits,
  useSpectrumHitsByScan,
} from "@/hooks/useSpectrumData";
import { useAnalysisStore } from "@/stores/useAnalysisStore";
import type { AnalysisFeature } from "@/types";
import type { FeatureData } from "@/types/spectrum";
import type { PreprocessingKey } from "@/types/spectrumComparison";
import { blobApiErrorDetail } from "@/utils/apiError";
import { dashboardGridColsClass } from "@/utils/dashboardLayout";
import { siblingCandidates } from "@/utils/observationGrouping";

function parseScenario(value: string | null): DemoScenario {
  if (value === "arson-strong" || value === "natural-strong") return value;
  return null;
}

/**
 * 3-col 영역의 카드 래퍼 — 테두리·elevation 만 그리고 **안쪽 여백은 그리지 않는다.**
 *
 * 2026-08-28: 여기 있던 `p-4`(한쪽 16px)는 이중 패딩이었다. 이 래퍼가 감싸는
 * 컴포넌트들은 전부 자기 뿌리에 `panelCls`(`p-3`)를 그리므로 패널마다 콘텐츠
 * 폭에서 56px 이 빠지고 있었다 — 흰 카드 안의 흰 카드라 눈에는 보이지 않고
 * 폭만 먹었다. 여백은 컴포넌트가 그리고, 페이지는 배치와 테두리만 맡는다.
 * (반경도 `rounded-lg`(8px) → `rounded-xl`(12px)로 panelCls 와 맞췄다. 여백이
 *  0 이 되면서 두 반경이 같은 모서리에 겹치기 때문이다.)
 *
 * 근거: docs/2026/260828_인수인계_사이드바접기_검토.md §2.
 */
const PANEL_CARD = "bg-white rounded-xl shadow-sm border border-gray-200";

/**
 * 좌·우 사이드바 — 데스크톱에서 스티키.
 * `min-w-0` 는 접힘 트랙(48px)보다 넓은 내용이 트랙을 밀어내지 못하게 한다.
 */
const SIDEBAR_ASIDE = "min-w-0 md:sticky md:top-20 md:self-start";

/**
 * 펼침 상태에서만 붙이는 스크롤 제한 — 패널이 화면보다 길면 패널 안에서 스크롤.
 *
 * **접힘 상태에는 붙이지 않는다.** `overflow-y-auto` 는 CSS 규정상 `overflow-x`
 * 까지 `auto` 로 끌어올려 사방이 잘리는 스크롤 컨테이너를 만든다. 레일 버튼은
 * aside 와 폭이 같아서(w-full) 전역 포커스 링(요소 바깥 4px)이 **통째로 잘려
 * 한 픽셀도 보이지 않았다** — 접힌 사이드바를 되돌리는 유일한 컨트롤인데
 * 키보드 사용자가 포커스 위치를 알 수 없게 된다(실측 확인). 레일은 짧아서
 * 스크롤이 필요하지도 않다.
 */
const SIDEBAR_SCROLL = "max-h-[calc(100vh-6rem)] overflow-y-auto";

export default function SampleAnalysisPage() {
  const { id: analysisId } = useParams<{ id: string }>();
  const numericAnalysisId = analysisId ? Number(analysisId) : undefined;

  // iter2.2: Demo Scenario Mode URL param
  const [searchParams] = useSearchParams();
  const scenario = parseScenario(searchParams.get("scenario"));

  // -------------------------------------------------------
  // Analysis detail (includes sample brief info)
  // -------------------------------------------------------
  const { data: analysisDetail, isLoading: isDetailLoading } =
    useAnalysisDetail(numericAnalysisId);

  // 분석 완료(running/pending → completed) 전환 시 차트 데이터 쿼리를 자동 무효화 →
  // 새로고침 없이 그래프가 채워지도록. (useAnalysisDetail은 상태만 polling)
  useRefetchChartsOnComplete(analysisDetail?.status);

  // -------------------------------------------------------
  // Chart interaction state (selectedFeature, selectedRT, brushRange, handlers)
  // -------------------------------------------------------
  const {
    selectedFeature,
    selectedRT,
    brushRange,
    handleFeatureSelect,
    handleMZRTClick,
    handleTICZoom,
    handleClearBrush,
  } = useChartInteraction();

  // -------------------------------------------------------
  // Data: API first, mockup fallback
  // -------------------------------------------------------
  const sampleId = analysisDetail?.sample?.id;

  // MS 원본 결측(D19): backend가 romsh_status="not_applicable_no_ms"로 명시 종결한
  // TIC-only 시료. 이 권위 신호로 MZRT/MS/동정/RoMSH를 정직하게 "미수집" 표시한다.
  const isMsMissingSample =
    analysisDetail?.romsh_status === "not_applicable_no_ms";

  // MZRT 표시 밀도: "standard"는 서버 기본 데시메이션, "detail"은 전체 피크(max_points 상향).
  // 점이 많아 느리고 안 보일 때만 사용자가 상세로 전환 — 기본은 표준.
  const [mzrtDensity, setMzrtDensity] = useState<MZRTDensity>("standard");
  const mzrtMaxPoints = mzrtDensity === "detail" ? 4000 : undefined;

  const {
    features,
    similarCases,
    ticData,
    mzrtData,
    massSpectrumData,
    isMsMissing,
    isLoading: isDataLoading,
    scenarioMockedProb,
    isScenarioActive,
  } = useMockupFallback({
    analysisId: numericAnalysisId,
    sampleId: sampleId ?? undefined,
    selectedRT: selectedRT ?? undefined,
    scenario,
    msMissing: isMsMissingSample,
    mzrtMaxPoints,
  });

  // iter2.2: scenario active 시 ConclusionStripV2의 RoMSH prob override
  const effectiveAnalysis = useMemo(() => {
    if (!analysisDetail) return null;
    if (isScenarioActive && scenarioMockedProb != null) {
      return {
        ...analysisDetail,
        romsh_status: "completed" as const,
        romsh_probability: scenarioMockedProb,
        romsh_label: (scenarioMockedProb >= 0.5 ? 1 : 0) as 0 | 1,
        // PLAN 2-9b: 시나리오 라벨은 prob 0.5 로 매기므로 실제 분석의 모델
        // 임계값·temperature 를 남기면 거짓 부등식이 된다(PR #64 회귀).
        romsh_threshold: null,
        romsh_temperature: null,
      };
    }
    return analysisDetail;
  }, [analysisDetail, isScenarioActive, scenarioMockedProb]);

  // -------------------------------------------------------
  // L2 fix (iter1): feature 선택 시 selectedRT를 cascade하여 MS 갱신
  //   - Toggle on: feature.rt로 setSelectedRT 후 toggle
  //   - Toggle off (같은 feature 재클릭): selectedRT 유지 (Q3 정책)
  // 회귀: handleFeatureSelect가 selectedFeature만 토글, selectedRT 미변경
  // -------------------------------------------------------
  // B-3: selectFeatureWithRT atomic action (Zustand store)
  // getState() bypasses stale closure — features comes from memo'd prop
  const handleFeatureSelectWithRT = useCallback(
    (featureId: string) => {
      const f = features.find((x) => x.feature_id === featureId);
      useAnalysisStore.getState().selectFeatureWithRT(featureId, f?.rt ?? null);
    },
    [features],
  );

  // TIC 임의 지점 클릭 → 분석의 실제 시료(API mzrtData)의 측정 RT 중 nearest로 snap.
  // 데이터 출처 selector 제거 후, 드롭다운(sampleSource)이 아니라 현재 분석의
  // 실측 MZRT scan RT를 기준으로 정렬한다 (차트 데이터와 동일 출처 → 좌표 일관).
  const handleTICChartClick = useCallback(
    (rt: number) => {
      if (mzrtData.length === 0) return;
      let nearestRT = mzrtData[0].rt;
      let minDiff = Math.abs(nearestRT - rt);
      for (const point of mzrtData) {
        const diff = Math.abs(point.rt - rt);
        if (diff < minDiff) {
          minDiff = diff;
          nearestRT = point.rt;
        }
      }
      // 2026-09-03: RT 만 옮기고 물질 선택을 남겨두면 패널이 서로 다른 스캔을
      // 가리킨다(실측: MS 는 RT 21.18 인데 컨텍스트 카드는 RT 11.69 의
      // Naphthalene 그대로). 기록된 관찰이 없는 지점을 고른 것이므로 물질
      // 선택을 **해제**한다 — 그래야 참조 순위 패널이 그 스캔 기준(스캔 앵커)
      // 으로 바뀌어 "분석되지 않은 구간에서도 순위를 본다"가 성립한다.
      useAnalysisStore.getState().selectScanOnly(nearestRT);
    },
    [mzrtData],
  );

  // JSD 거리가 가장 작은 후보. 진입 시 이 후보가 자동 선택된다.
  //
  // 2026-08-28 (P2-3): 옛 이름은 `recommendedFeature`("추천 피크 — 가장 신뢰도
  // 높은 매칭")였다. 그러나 이 값은 **거리 최솟값일 뿐 신뢰도가 아니다** —
  // JSD 경계값(0.2/0.5)은 우리가 임의로 정한 값이라 2026-08-27 에 해석 라벨
  // 표시를 보류했고(`SHOW_JSD_LABEL`), 같은 근거가 "추천"·"가장 신뢰도 높은"
  // 이라는 이름·문구에도 그대로 적용된다.
  //
  // 동점 처리도 근거가 없다: 아래 reduce 는 strict `<` 라 값이 같으면 배열에서
  // 먼저 나온 후보가 이긴다. 그 배열 순서는 백엔드 `sort_results_for_features`
  // (RT 오름차순)이므로, 실질 동점 구간에서는 "가장 먼저 용출된 스캔"이 뽑힌다.
  // 프로덕션 실측상 다후보 관찰의 절반(50.6%)이 1·2위 격차 0.01 미만이다.
  // 인하대가 신뢰도 척도를 정의하면 그 기준으로 다시 정한다.
  const lowestJsdFeature = useMemo<FeatureData | null>(() => {
    const withJsd = features.filter((f) => f.jsd_distance != null);
    if (withJsd.length === 0) return null;
    return withJsd.reduce((prev, cur) =>
      // biome-ignore lint/style/noNonNullAssertion: filtered above
      cur.jsd_distance! < prev.jsd_distance! ? cur : prev,
    );
  }, [features]);

  // 분석 화면 진입 — 상호작용 상태의 소속 분석을 store 에 기록한다.
  // 다른 분석이면 이전 분석의 선택·RT·브러시를 버리고, 같은 분석이면 그대로 둔다.
  // (2026-08-28: store 가 모듈 전역이라 분석 A 의 feature_id 가 B 로 넘어가
  //  B 의 다른 물질이 선택된 것처럼 보이던 문제. feature_id 는 분석마다 F1·F2…
  //  로 재사용되므로 번호 충돌이 흔하다.)
  useEffect(() => {
    if (numericAnalysisId == null) return;
    useAnalysisStore.getState().enterAnalysis(numericAnalysisId);
  }, [numericAnalysisId]);

  // sprint22 #8: 페이지 진입 시 JSD 최소 후보 자동 선택.
  // 첫 클릭 시 selectedFeatureId null→value 전환의 큰 시각 변화를 없애고,
  // 상세·비교·순위 패널이 빈 채로 시작하지 않게 한다.
  //
  // 2026-08-28 (P2-3): 옛 가드는 `selectedFeature == null` 이라 **선택이 비는
  // 순간마다** 다시 걸렸다. 그래서 상세 패널의 닫기 버튼이 동작하지 않았고
  // (닫으면 즉시 재선택), 다른 후보를 보다 닫으면 JSD 최소 후보로 튀었다.
  //
  // 이제 "이 분석에서 이미 자동 선택을 걸었는가"를 **store 에** 둔다(`autoSelectDone`).
  // ref 로 두면 언마운트 때 사라져, 화면을 잠깐 벗어났다 돌아왔을 때 사람이 고른
  // 선택을 자동 선택이 덮어쓴다 — 선택 상태는 store 라 살아남기 때문이다.
  // store 에 두면 세 가지가 모두 맞는다:
  //   · 진입 1회만 걸린다(해제는 해제인 채로 남는다)
  //   · 같은 분석 재진입 — 사람이 고른 선택이 그대로 보존된다
  //   · 다른 분석 진입 — `enterAnalysis` 가 상태를 비우므로 새 분석 기준으로 다시 걸린다
  useEffect(() => {
    if (numericAnalysisId == null) return;
    const store = useAnalysisStore.getState();
    if (store.analysisId !== numericAnalysisId) return; // enterAnalysis 반영 전
    if (store.autoSelectDone) return;
    if (lowestJsdFeature == null) return;
    store.markAutoSelectDone();
    // 사람이 이미 고른 것이 있으면 존중한다(빈 상태일 때만 화면이 고른다).
    if (store.selectedFeatureId != null) return;
    store.selectFeatureWithRT(
      lowestJsdFeature.feature_id,
      lowestJsdFeature.rt ?? null,
    );
  }, [numericAnalysisId, lowestJsdFeature]);

  // P1-1: stable references for memo'd child components
  const analysisFeatures = useMemo(
    () => features as AnalysisFeature[],
    [features],
  );
  // S2: 선택된 후보(AI 동정 후보 표의 행)의 실험↔참조 스펙트럼 비교.
  // mockup fallback 데이터에는 result_id 가 없어 요청이 나가지 않는다 —
  // 없는 근거로 비교 화면을 열지 않기 위해서다.
  const selectedFeatureData = useMemo<FeatureData | null>(
    () =>
      selectedFeature
        ? ((features.find(
            (f) => f.feature_id === selectedFeature,
          ) as FeatureData) ?? null)
        : null,
    [selectedFeature, features],
  );

  // T1: 같은 스캔(관찰)의 다른 후보 — 상세 패널에서 나열·이동
  const selectedSiblings = useMemo<FeatureData[]>(
    () =>
      selectedFeatureData
        ? siblingCandidates(features as FeatureData[], selectedFeatureData)
        : [],
    [features, selectedFeatureData],
  );

  // T2-3: 판정 — 감정인 입력. 판정 목록은 result_id 로 조인, 현재 = 최신 1건.
  const { permissions } = useAuth();
  const assessments = useAssessments(numericAnalysisId);
  const createAssessment = useCreateAssessment(numericAnalysisId);
  const currentAssessment = useMemo(() => {
    const resultId = selectedFeatureData?.result_id;
    if (resultId == null) return null;
    return (
      assessments.data?.current.find((a) => a.result_id === resultId) ?? null
    );
  }, [assessments.data, selectedFeatureData?.result_id]);
  const assessmentCount = useMemo(() => {
    const resultId = selectedFeatureData?.result_id;
    if (resultId == null) return 0;
    return (
      assessments.data?.history.filter((a) => a.result_id === resultId)
        .length ?? 0
    );
  }, [assessments.data, selectedFeatureData?.result_id]);
  const handleSubmitAssessment = useCallback(
    (status: string, rationale: string) => {
      const resultId = selectedFeatureData?.result_id;
      if (resultId == null) return;
      createAssessment.mutate({ resultId, status, rationale });
    },
    [createAssessment, selectedFeatureData?.result_id],
  );

  // S7: 히트 목록에서 고른 후보. null 이면 AI 후보와 비교한다.
  const [selectedHitCompoundId, setSelectedHitCompoundId] = useState<
    number | null
  >(null);

  // 물질을 바꾸면 후보 선택을 초기화한다 — 이전 물질의 후보가 남으면 안 된다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: result_id 변경에만 반응한다
  useEffect(() => {
    setSelectedHitCompoundId(null);
  }, [selectedFeatureData?.result_id]);

  const spectrumComparison = useSpectrumComparison(
    numericAnalysisId,
    selectedFeatureData?.result_id,
    selectedHitCompoundId ?? undefined,
  );
  // 순위의 기본 전처리는 배경가스 제외다(운영자 결정 2026-08-19) —
  // 1:1 비교 화면의 기본(원자료)과 다르며, 그 사실은 목록에 표기된다.
  const [hitPreprocessing, setHitPreprocessing] = useState<PreprocessingKey>(
    "background_excluded",
  );
  const spectrumHits = useSpectrumHits(
    numericAnalysisId,
    selectedFeatureData?.result_id,
    hitPreprocessing,
  );

  // 검출 표시 개선 1단계 (2026-08-31): 후보 선택이 없어도 선택 RT 가 있으면
  // 그 스캔의 참조 전량 순위를 보여준다. 후보 전원이 기준 미달이라 행이 남지
  // 않은 스캔(이전엔 열람 불가 — 동정 대상 스캔의 61%)도 TIC 클릭으로 열린다.
  // 후보를 고르면 후보 앵커가 우선한다. MS 결측 시료는 스캔 자체가 없어 제외.
  const scanRtForHits =
    selectedFeatureData?.result_id == null && selectedRT != null && !isMsMissing
      ? selectedRT
      : undefined;
  const scanSpectrumHits = useSpectrumHitsByScan(
    numericAnalysisId,
    scanRtForHits,
    hitPreprocessing,
  );

  // -------------------------------------------------------
  // Filtered m/z-RT data driven by the TIC brush range
  // -------------------------------------------------------
  // 2026-09-03: 브러시가 RT 도메인이라 TIC 배열을 거치지 않는다 — 표시
  // 해상도가 바뀌어도 같은 구간을 가리킨다.
  const filteredMzrtData = useMemo(() => {
    if (!brushRange) return mzrtData;
    return mzrtData.filter(
      (item) => item.rt >= brushRange.rtMin && item.rt <= brushRange.rtMax,
    );
  }, [mzrtData, brushRange]);

  // -------------------------------------------------------
  // Brush filter chip label (Scenario B)
  // -------------------------------------------------------
  const brushActiveLabel = useMemo(() => {
    if (!brushRange) return null;
    return `RT ${brushRange.rtMin.toFixed(1)}~${brushRange.rtMax.toFixed(1)} 필터링됨`;
  }, [brushRange]);

  // -------------------------------------------------------
  // Report download
  // -------------------------------------------------------
  const { download: downloadPptx } = useDownloadAnalysisPptx();
  const [isDownloading, setIsDownloading] = useState(false);
  // 내려받기 실패 사유 — 종전에는 콘솔에만 남아 화면이 침묵했다(PLAN 2-3 후속)
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // 접기 상태는 **브라우저에 남는다** — 2026-09-04 운영자 요청으로 정책 변경.
  //
  // 운영자: "접기가 있는 스티키 메뉴, 보조지표 등은 직전 상태를 유지하도록
  // 만들 수 있나요? 새로고침하면 다 원상복귀돼서 좀 불편하네."
  //
  // 2026-08-28 에는 **저장하지 않기로** 했었다. 그때의 이유와 지금의 판단:
  //   ① "같은 URL 이 사람마다 다르게 보인다 — 시연에서 '내 화면엔 그게 없다'"
  //      → 여전히 유효한 위험이다. 다만 이것은 **시연 장비를 한 번 맞춰 두면**
  //        되는 문제이고, 매번 접었다 펴야 하는 불편은 매일 발생한다.
  //        10월 시연 전에 시연 장비에서 원하는 배치로 한 번 맞춰 둘 것.
  //   ② "전역 store 에 넣으면 테스트 beforeEach 가 리셋해 사각지대가 된다"
  //      → 이 방식은 store 가 아니라 컴포넌트 지역 상태 + localStorage 다.
  //        저장이 막혀 있어도 기본값으로 동작한다(usePersistentBoolean).
  //
  // **선택한 후보·RT·확대 구간은 저장하지 않는다.** 그건 배치가 아니라 판독
  // 상태이고, 다시 열었을 때 이전 선택이 남아 있으면 지금 보고 있는 것을
  // 오해하게 된다. 저장 대상은 "패널이 접혀 있는가" 뿐이다.
  // 근거: docs/2026/260828_인수인계_사이드바접기_검토.md §6-3 (뒤집힘).
  const [isAiSectionExpanded, setIsAiSectionExpanded] = usePersistentBoolean(
    "toxgas.dashboard.aiSectionExpanded",
    true,
  );
  const [isLeftCollapsed, setIsLeftCollapsed] = usePersistentBoolean(
    "toxgas.dashboard.leftCollapsed",
    false,
  );
  const [isRightCollapsed, setIsRightCollapsed] = usePersistentBoolean(
    "toxgas.dashboard.rightCollapsed",
    false,
  );

  const handleDownloadReport = async () => {
    if (!numericAnalysisId) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      await downloadPptx(numericAnalysisId);
    } catch (error) {
      // 미완료 분석 400 등 서버 사유를 보여 준다 — 응답이 Blob 이라 본문을 읽는다
      setDownloadError(
        await blobApiErrorDetail(
          error,
          "보고서를 내려받지 못했습니다. 잠시 후 다시 시도하세요.",
        ),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // -------------------------------------------------------
  // Derive the SampleInfo collapsed header data
  // -------------------------------------------------------
  const sampleInfoSummary = useMemo(() => {
    if (!analysisDetail?.sample) return null;
    const s = analysisDetail.sample;
    return {
      sampleId: s.sample_id,
      name: s.name,
      analysisStatus: analysisDetail.status,
      // 좌측 사이드바를 접어도 남아야 하는 고유 정보 (헤더 칩으로 승격).
      // 시료 상태는 분석 상태와 다른 사실이다 — 둘 다 있어야 접기가 무손실이다.
      sampleType: s.sample_type,
      collectionLocation: s.collection_location,
      sampleStatus: s.status,
    };
  }, [analysisDetail]);

  // -------------------------------------------------------
  // Derive the SampleInfo prop shape from the analysis detail (PI 원안 복원)
  // SampleInfo 컴포넌트는 { id: string, name, sample_type, status } 형태를 기대.
  // SampleBrief는 sample_id(string) + id(number) — id 필드로 매핑.
  // -------------------------------------------------------
  const sampleInfoData = useMemo(() => {
    if (!analysisDetail?.sample) return undefined;
    const s = analysisDetail.sample;
    return {
      id: s.sample_id,
      name: s.name,
      sample_type: s.sample_type,
      status: s.status,
      collection_location: s.collection_location ?? undefined,
    };
  }, [analysisDetail]);

  // -------------------------------------------------------
  // Loading state
  // -------------------------------------------------------
  const isLoading = isDetailLoading || isDataLoading;

  if (isLoading) {
    return <AnalysisLoadingState />;
  }

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------
  return (
    <div>
      {/* ===== Breadcrumb ===== */}
      <AnalysisBreadcrumb analysisId={analysisId} />

      {/* ===== Header (SampleInfoCollapsed + 보고서 다운로드) ===== */}
      <div className="mb-4">
        <div className="flex items-center justify-between gap-4">
          {/* 시료 정보 1줄 요약 (SampleInfoCollapsedHeader) */}
          <SampleInfoCollapsedHeader
            sampleInfoSummary={sampleInfoSummary}
            analysisId={analysisId}
          />

          {/* T3: 모드 표식 — 이 화면은 위해기체·단일화합물 모드다 (기획 v4 §4).
              화재잔해 클래스 검토는 사건 상세의 [화재잔해 클래스 모드]에서. */}
          <span className="shrink-0 hidden md:inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
            위해기체·단일화합물 모드
          </span>

          {/* Download report button */}
          <button
            type="button"
            onClick={handleDownloadReport}
            disabled={isDownloading || !numericAnalysisId}
            className="shrink-0 bg-white text-red-700 border border-red-300 px-5 py-2 rounded-lg font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isDownloading ? (
              <>
                <svg
                  aria-label="Loading"
                  role="img"
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>다운로드 중...</span>
              </>
            ) : (
              <>
                <svg
                  role="img"
                  aria-label="Download"
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>보고서 다운로드</span>
              </>
            )}
          </button>
        </div>
      </div>

      {downloadError && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
        >
          {downloadError}
        </p>
      )}

      {/* iter2.2: Demo Scenario Mode banner — forensic 정직성 명시 */}
      <DemoScenarioBanner
        isScenarioActive={isScenarioActive}
        scenario={scenario}
      />

      {/* 결과에 포함되지 않은 것 + 적용 기준 — 완료 노트·증거 스냅숏 표면화 */}
      <AnalysisGapsNote
        status={analysisDetail?.status}
        errorMessage={analysisDetail?.error_message}
        evidence={analysisDetail?.evidence}
      />

      {/* ===== S23-E: 팩트 요약 카드 (상단, 방화 단정 없음) ===== */}
      <div className={`mb-4 ${PANEL_CARD} p-4`}>
        <FactSummaryCard features={features as FeatureData[]} />
      </div>

      {/* ===== S23-E: AI 보조 분석 지표 (하단 접이식) / 분석 진행 표시기 ===== */}
      <div className={`mb-6 ${PANEL_CARD}`}>
        {/* 섹션 헤더 — 접이식 토글 */}
        <button
          type="button"
          onClick={() => setIsAiSectionExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-xl"
          aria-expanded={isAiSectionExpanded}
          aria-controls="ai-aux-section-content"
        >
          <span className="text-xs font-black uppercase tracking-widest text-gray-400">
            AI 보조 분석 지표 (연구용) — 인화성 물질 패턴 유사도
          </span>
          <span className="text-xs text-gray-400">
            {isAiSectionExpanded ? "▾ 접기" : "▸ 펼치기"}
          </span>
        </button>

        {/* 섹션 본문 */}
        {isAiSectionExpanded && (
          <div
            id="ai-aux-section-content"
            className="px-4 pb-4 border-t border-gray-100"
          >
            {effectiveAnalysis &&
            (effectiveAnalysis.status === "pending" ||
              effectiveAnalysis.status === "running") ? (
              <div className="pt-4">
                <AnalysisProgressIndicator
                  status={effectiveAnalysis.status}
                  startedAt={effectiveAnalysis.started_at ?? null}
                />
              </div>
            ) : (
              <div className="pt-3">
                <ConclusionStripV2
                  analysis={
                    effectiveAnalysis ?? {
                      romsh_status: null,
                      romsh_probability: null,
                      romsh_label: null,
                      romsh_threshold: null,
                      romsh_temperature: null,
                      romsh_model_version: null,
                      romsh_error_message: null,
                    }
                  }
                  features={analysisFeatures}
                  romshInput={
                    analysisDetail && !isScenarioActive
                      ? {
                          evidence: analysisDetail.evidence,
                          selectedFeatureId: selectedFeature,
                          onSelectFeature: handleFeatureSelectWithRT,
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== 3-col sticky sidebar layout =====
          트랙 폭은 `dashboardLayout.ts` 가 정한다. 펼침 상태는 기존
          `grid-cols-12` + `col-span-2/7/3` 과 픽셀 동일하다 — 3-col 구조는
          2026-05-15 에 사용자 시각 검증으로 채택된 안이라 접기를 넣으면서
          폭이 달라지면 안 된다. */}
      <div
        data-testid="dashboard-3col-grid"
        className={`grid grid-cols-1 gap-5 items-start ${dashboardGridColsClass(
          { left: isLeftCollapsed, right: isRightCollapsed },
        )}`}
      >
        {/* Left sidebar: SampleInfo — sticky on desktop */}
        <aside
          className={`${SIDEBAR_ASIDE} ${isLeftCollapsed ? "" : SIDEBAR_SCROLL}`}
        >
          {isLeftCollapsed ? (
            <SidebarRail
              label="샘플 정보"
              side="left"
              controls="sample-info-panel"
              onExpand={() => setIsLeftCollapsed(false)}
              cardClassName={PANEL_CARD}
            />
          ) : (
            <div id="sample-info-panel" className={PANEL_CARD}>
              <SampleInfo sample={sampleInfoData ?? undefined} />
              <SidebarCollapseButton
                label="샘플 정보"
                side="left"
                controls="sample-info-panel"
                onCollapse={() => setIsLeftCollapsed(true)}
              />
            </div>
          )}
        </aside>

        {/* Center: chart area */}
        <div className="space-y-6">
          {/* MZRTPlot (center 전폭) */}
          <div className={PANEL_CARD}>
            <MZRTPlot
              filteredMzrtData={filteredMzrtData}
              features={features}
              selectedFeature={selectedFeature}
              isMsMissing={isMsMissing}
              onMZRTClick={handleMZRTClick}
              brushActiveLabel={brushActiveLabel}
              onClearBrush={handleClearBrush}
              density={mzrtDensity}
              onDensityChange={setMzrtDensity}
            />
          </div>

          {/* TICPlot | MassSpectrum — 7:5 (2026-09-06, 운영자 승인).
              6:6 에서는 TIC 이 그리는 폭이 221px 뿐이라 1픽셀에 스캔 16개가
              겹쳤다(원자료 12,270점). 드래그·클릭 대상이 바로 이 차트다.
              8:4 는 질량스펙트럼의 10px 고정 막대가 서로 겹쳐 못 쓴다.
              배분·하한은 utils/dashboardLayout.ts 가 SSOT — 테스트가 고정한다. */}
          <div className="grid grid-cols-12 gap-5">
            <div className={`col-span-7 ${PANEL_CARD}`}>
              {/* 2026-09-03: recharts(SVG) → uPlot(Canvas) 전량 렌더.
                  서버 축약 없이 원자료 12,000점대를 그리고, 브러시는 RT
                  도메인을 돌려준다(외부 자문 반영). */}
              <TICPlotUplot
                ticData={ticData}
                features={features}
                selectedFeature={selectedFeature ?? undefined}
                selectedScanRT={selectedRT}
                onBrushChange={handleTICZoom}
                onFeatureSelect={handleFeatureSelectWithRT}
                onChartClick={handleTICChartClick}
              />
            </div>
            <div className={`col-span-5 ${PANEL_CARD}`}>
              <MassSpectrum
                massSpectrumData={massSpectrumData}
                selectedFeature={selectedFeature}
                features={features}
                selectedRT={selectedRT}
                isMsMissing={isMsMissing}
              />
            </div>
          </div>

          {/* ── 선택 드라이버가 먼저 온다 (P1-2 · 2026-08-28) ──
              「AI 동정 후보」 표 + 상세 패널 → 「참조 스펙트럼 순위」 + 스펙트럼 비교.

              2026-08-28 이전에는 순위·비교 행이 이 표보다 **위**에 있었다. 그런데
              순위·비교는 이 표에서 고른 후보의 `result_id` 에 종속돼 있어(위
              useSpectrumComparison / useSpectrumHits), 소비자가 생산자보다 위에
              놓인 역순이었다. 화면을 위에서 아래로 읽으면 "무엇을 눌러야 아래
              두 패널이 채워지는지" 알 수 없었다.
              근거: docs/2026/260827_대시보드_두섹션_필요성_분석.md P1-2. */}

          {/* AI 동정 후보 표 + FeatureDetailPanel */}
          {/* K2: hybrid responsive — desktop(>=lg) col-5 panel / tablet-mobile(<lg) expand-row inside FeaturesTable
              iter2 B-2: panel 항상 mount → desktop 항상 7+5 grid (placeholder 시에도 우측 영역 활용) */}
          {/* S24: 후보 표 폭 확대 (7:5 → 8:4) — 1440px급에서 테이블
              가로 스크롤 완화. 컬럼 접힘은 FeaturesTable 컨테이너 쿼리가 담당. */}
          <div className="grid grid-cols-12 gap-5">
            <div className={`col-span-12 lg:col-span-8 ${PANEL_CARD}`}>
              <FeaturesTable
                features={features}
                selectedFeature={selectedFeature}
                onFeatureSelect={handleFeatureSelectWithRT}
                isMsMissing={isMsMissing}
                inlineDetailRenderer={(feature) => (
                  <FeatureDetailInlineRow
                    key={`inline-${feature.feature_id}`}
                    feature={feature}
                  />
                )}
              />
            </div>
            {/* FeatureDetailPanel: desktop only
                iter2 B-2: 항상 mount — feature=null 시 placeholder + JSD 최소 후보 프리뷰 */}
            <div className={`hidden lg:block lg:col-span-4 ${PANEL_CARD}`}>
              <FeatureDetailPanel
                feature={selectedFeatureData}
                onClose={() =>
                  selectedFeature && handleFeatureSelect(selectedFeature)
                }
                siblings={selectedSiblings}
                onSelectSibling={handleFeatureSelectWithRT}
                assessment={currentAssessment}
                assessmentCount={assessmentCount}
                canAssess={
                  !!permissions?.canAssessCandidates &&
                  selectedFeatureData?.result_id != null
                }
                onSubmitAssessment={handleSubmitAssessment}
                assessmentSubmitting={createAssessment.isPending}
                assessmentError={
                  createAssessment.isError
                    ? "판정 기록에 실패했습니다 — 입력은 보존됩니다. 다시 시도하세요."
                    : null
                }
                lowestJsdFeature={lowestJsdFeature}
                onSelectLowestJsd={
                  lowestJsdFeature
                    ? () =>
                        handleFeatureSelectWithRT(lowestJsdFeature.feature_id)
                    : undefined
                }
              />
            </div>
          </div>

          {/* S2·S7: 참조 스펙트럼 순위 + 스펙트럼 비교.
              원본(NIST MS Search)의 좌측 Hit List + 우측 비교 pane 배치를 따른다 —
              "무엇과 닮았는지"를 먼저 보고 하나를 골라 자세히 본다.
              P1-2: 위 후보 표에서 고른 result_id 에 종속되므로 그 표 아래에 둔다. */}
          <div className="grid grid-cols-12 gap-5">
            <div className={`col-span-12 lg:col-span-4 ${PANEL_CARD}`}>
              {/* 2026-08-28: 선택이 없으면 두 패널에 데이터를 넘기지 않는다.
                  두 쿼리는 `placeholderData: keepPreviousData` 라(useSpectrumData.ts)
                  선택이 풀려 쿼리가 비활성화돼도 **직전 후보의 순위·비교가 그대로
                  남는다**. 상세 패널은 비고 표의 강조도 풀린 채 순위표만 남으면,
                  화면이 "아무것도 안 골랐는데 이 물질의 순위"를 말하는 자기모순이
                  된다. 선택 해제가 실제로 가능해지면서(P2-3) 도달 가능해진 상태다. */}
              {/* 스캔 앵커에서는 직전 후보의 비교 데이터(keepPreviousData)로
                  하이라이트가 남고, 클릭해도 비교가 열리지 않는다 — 후보
                  앵커일 때만 선택·클릭을 배선한다 (교차 검토 확정 #10). */}
              <SpectrumHitList
                hits={
                  selectedFeatureData?.result_id != null
                    ? spectrumHits.data
                    : scanRtForHits != null
                      ? scanSpectrumHits.data
                      : null
                }
                isLoading={
                  selectedFeatureData?.result_id != null
                    ? spectrumHits.isLoading
                    : scanRtForHits != null
                      ? scanSpectrumHits.isLoading
                      : false
                }
                selectedCompoundId={
                  selectedFeatureData?.result_id != null
                    ? (spectrumComparison.data?.reference?.compound_id ?? null)
                    : null
                }
                onSelect={
                  selectedFeatureData?.result_id != null
                    ? setSelectedHitCompoundId
                    : undefined
                }
                onPreprocessingChange={setHitPreprocessing}
              />
            </div>
            <div className={`col-span-12 lg:col-span-8 ${PANEL_CARD}`}>
              <SpectrumComparison
                comparison={
                  selectedFeatureData?.result_id != null
                    ? spectrumComparison.data
                    : null
                }
                isLoading={spectrumComparison.isLoading}
              />
            </div>
          </div>

          {/* SecondaryInsightsPeek — Phase 3a P0 */}
          <div className={`${PANEL_CARD} p-4`}>
            <SecondaryInsightsPeek
              similarCases={similarCases}
              features={features}
            />
          </div>

          {/* SimilarCaseDB | MaterialDistribution */}
          <div className="grid grid-cols-12 gap-5">
            <div className={`col-span-6 ${PANEL_CARD}`}>
              <SimilarCaseDB
                similarCases={similarCases}
                isMsMissing={isMsMissing}
              />
            </div>
            <div className={`col-span-6 ${PANEL_CARD}`}>
              <MaterialDistribution
                features={features}
                isMsMissing={isMsMissing}
              />
            </div>
          </div>
        </div>

        {/* Right sidebar: AIAnalysisSummary — sticky on desktop */}
        <aside
          className={`${SIDEBAR_ASIDE} ${isRightCollapsed ? "" : SIDEBAR_SCROLL}`}
        >
          {isRightCollapsed ? (
            <SidebarRail
              label="AI 분석 요약"
              side="right"
              controls="ai-summary-panel"
              onExpand={() => setIsRightCollapsed(false)}
              cardClassName={PANEL_CARD}
            />
          ) : (
            <div
              id="ai-summary-panel"
              data-testid="ai-summary-panel"
              className={PANEL_CARD}
            >
              <AIAnalysisSummary
                selectedFeature={selectedFeature}
                features={features}
                analysisStatus={analysisDetail?.status}
                isMsMissing={isMsMissing}
              />
              <SidebarCollapseButton
                label="AI 분석 요약"
                side="right"
                controls="ai-summary-panel"
                onCollapse={() => setIsRightCollapsed(true)}
              />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
